import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUpdateOrganizationMembership } from 'api/OrganizationAPI';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer/utils';
import { Modal, ModalBody, ModalHeader, ModalFooter, MODAL_SIZES } from 'ui/modal';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { Add16 } from 'ui/icons/action/Add16';
import { BasicSelect } from 'ui/selects/BasicSelect';
import { FormField } from 'ui/formField';
import { Body2 } from 'ui/theme/typography';
import { fullName } from '../../utils';
import {
  useOrganizationModalsContext,
  MemberProjectPayload,
} from '../../contexts/OrganizationModalsContext';
import { useOrganizationMembersQueryContext } from '../../contexts/OrganizationMembersQueryContext';
import { Role } from '../../types';
import { combineProjectAndTeamRoles, getTeamRoleByName, getProjectRoleByName } from './utils';

export const AddToProjectModal = () => {
  const { modalState, hideModal } = useOrganizationModalsContext();
  const { roles, refetchAll } = useOrganizationMembersQueryContext();
  const dispatch = useDispatch();
  const [roleName, setRoleName] = useState<string | null>(null);

  const member = (modalState?.payload as MemberProjectPayload)?.member;
  const project = (modalState?.payload as MemberProjectPayload)?.project;

  const { mutate, isLoading } = useUpdateOrganizationMembership(member?.userId, {
    onSuccess: () => {
      dispatch(
        addPositiveToast(
          `${fullName(member)} was added to the ${project?.name} project successfully.`,
        ),
      );
      refetchAll();
      hideModal();
    },
    onError: (error: unknown) => {
      const msg = `There was an error adding ${fullName(member)} to the ${
        project?.name
      } project. Please try again.`;
      dispatch(addNegativeToastWithErrorId(msg, 'addMemberToProject', error));
      hideModal();
    },
  });

  const handleSelectRole = (selectedValue: string[]) => {
    const [value] = selectedValue;
    setRoleName(value);
  };

  const combinedRoles = combineProjectAndTeamRoles(roles);
  const sortedCombinedRoles = [...combinedRoles].sort((a, b) => a.id - b.id);

  const roleOptions = sortedCombinedRoles.map((role: Role) => ({
    id: String(role.id),
    value: role.name,
    label: role.name,
  }));

  const onSubmit = () => {
    if (!roleName) return;

    const projectRole = getProjectRoleByName(roles, roleName);
    const teamRole = getTeamRoleByName(roles, roleName);

    const projectMemberships = [
      ...member.projectMemberships,
      ...(projectRole ? [{ projectId: project.id, roleId: projectRole.id }] : []),
    ];
    const teamMemberships = [
      ...member.teamMemberships,
      ...(teamRole
        ? [{ projectId: project.id, teamId: project.rootTeamId, roleId: teamRole.id }]
        : []),
    ];
    const payload = {
      projectMemberships,
      teamMemberships,
    };

    mutate(payload);
  };

  return (
    <Modal id="add-to-project-modal" onClose={hideModal} isModalOpen size={MODAL_SIZES.SMALL}>
      <ModalHeader>Add to project</ModalHeader>
      <ModalBody>
        <Body2>
          Assign role for the <b>{project?.name}</b> project.
        </Body2>
        <FormField labelText="Role" inputId="role">
          <BasicSelect
            id="role"
            placeholder="Select a role..."
            isFullWidth
            options={roleOptions}
            onSelect={handleSelectRole}
          />
        </FormField>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={onSubmit}
          size={BUTTON_SIZES.LARGE}
          isDisabled={!roleName || isLoading}
          isLoading={isLoading}
        >
          <Add16 />
          Add to project
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
